import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDropzone } from "react-dropzone";
import parse from "html-react-parser";
import { FileType } from "../helpers/types";
import { useTranslation } from "react-i18next";
import { useState } from 'react';

type Props = {
    extensionsFormat: string[];
    onHandleDrop: (files: (FileType & { preview: string })[]) => void;
    onRemoveFile: (index: number) => void;
    files: (FileType & { preview: string })[]; // Can be single or multiple files
    error: string | null;
    placeholder: string;
    name: string;
    maxFileSizeMB?: number;
    label?: string;
    description?: string;
};

const FileUpload = (props: Props) => {
    const { extensionsFormat, onHandleDrop, onRemoveFile, files, error, placeholder, name, label, description } = props;
    const { t } = useTranslation();

    const extensionToMime: { [key: string]: string } = {
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'doc': 'application/msword',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'xlsm': 'application/vnd.ms-excel',
        'csv': 'text/csv',
        'xls': 'application/vnd.ms-excel',
        'ppt': 'application/vnd.ms-powerpoint',
        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'pdf': 'application/pdf',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'mp4': 'video/mp4'
    };

    const acceptedTypes = extensionsFormat.reduce((acc: { [key: string]: any }, ext: string) => {
        const mimeType = extensionToMime[ext.replace('.', '')];
        if (mimeType) {
            acc[mimeType] = [];
        }
        return acc;
    }, {});


    const maxSizeBytes = (props.maxFileSizeMB ?? 100) * 1024 * 1024;

    const [dropError, setDropError] = useState<string | null>(null);

    const { getRootProps, getInputProps } = useDropzone({
        accept: acceptedTypes,
        maxSize: maxSizeBytes,
        onDrop: (acceptedFiles) => {
            setDropError(null);
            const filesWithPreview = acceptedFiles.map((file) =>
                Object.assign(file, { preview: URL.createObjectURL(file) })
            );

            onHandleDrop(filesWithPreview);
        },
        onDropRejected: (fileRejections) => {
            if (!fileRejections || fileRejections.length === 0) return;
            const first = fileRejections[0];
            const sizeErr = first.errors.find((e) => e.code === 'file-too-large');
            if (sizeErr) {
                setDropError(
                    t("dropzone.file_too_large", {
                        max: props.maxFileSizeMB ?? 100
                    })
                );
                return;
            }
            const msgs = first.errors.map((e) =>
                t(`dropzone.errors.${e.code}`, e.message)
            );
            setDropError(msgs.join(", ") || t("dropzone.file_rejected"));
        },
    });

    const handleRemoveClick = (event: React.MouseEvent<HTMLDivElement>, index: number) => {
        event.stopPropagation();
        onRemoveFile(index);
    };

    return (
        <>
            <label className="form-label">{label}</label>
            {description && <div className="text-muted small mb-2">{parse(description)}</div>}
            <div className={`dropzone ${error ? 'is-invalid' : files?.length > 0 ? 'is-valid' : ''}`}  {...getRootProps()}>
                <div role="presentation" tabIndex={0} className={`file-upload-container ${files && files.length > 0 && 'file-upload-container-multiple'}`}>
                    <input
                        accept=".jpg"
                        className="d-none"
                        name={name}
                        {...getInputProps()}
                        multiple={true}
                    />

                    <div className="file-wrapper d-flex align-items-center justify-content-center">
                        <div
                            className="file-upload-icon d-flex align-items-center justify-content-center border border-primary rounded-pill me-2"
                            style={{ width: 50, height: 50 }}
                        >
                            <FontAwesomeIcon icon={["fal", "file-arrow-up"]} size="xl" />
                        </div>
                        <span className="text-primary" style={{ fontSize: 20 }}>
                            {placeholder ? placeholder : t('upload_file_placeholder')}
                            <p className="mb-0 text-body opacity-50" style={{ fontSize: 12 }}>
                                {t('approved_files')}
                                {extensionsFormat.map((ext: string, index: number) => (
                                    <span key={ext}>
                                        {ext.replace('.', '')}
                                        {index < extensionsFormat.length - 1 && ', '}
                                    </span>
                                ))}
                                <br />
                                {t('max_file_size')}: {props.maxFileSizeMB ?? 100}MB
                            </p>
                        </span>
                    </div>

                    {files && files.length > 0 && (
                        <div className="mt-5 row w-100">
                            {files.map((file, index) => (
                                <div className="col-12 multiple-file-col" key={index}>
                                    <div className="d-flex justify-content-between w-100 py-2">
                                        <span className="fw-semibold small">{file.name}</span>

                                        <div className="text-danger" onClick={(e) => handleRemoveClick(e, index)}>
                                            <FontAwesomeIcon icon={["fal", "trash"]} />
                                            <span className="ms-1 small">{t('delete')}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {error && <div className="invalid-feedback">{parse(error)}</div>}
            {dropError && <div className="text-danger">{parse(dropError)}</div>}
        </>
    );
};

export default FileUpload;